import React, { FC, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddRounded,
  ChatBubbleOutlineRounded,
  CheckRounded,
  CloseRounded,
  DeleteOutlineRounded,
  EditOutlined,
  EventRepeatOutlined,
  MenuBookOutlined,
  MoreVertRounded,
  PsychologyOutlined,
  PushPinOutlined,
  PushPin,
  SearchRounded,
} from "@mui/icons-material";
import color from "../../Constants/colors";
import {
  useComoConversations,
  useDeleteComoConversation,
  useSetConversationPin,
  useUpdateComoConversation,
} from "../../Query/useComoAgent";
import { ComoConversation } from "../../Models/comoAgent";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import {
  DashboardMenu,
  DashboardMenuLabel,
  itemSx,
  sidebarMenuPaperSx,
} from "../DashboardMenu/DashboardMenu";
import { NavItem } from "../DashboardMenu/navigationItems";
import { useSidebarNavState } from "../../Contexts/SidebarNavContext";

const COLLAPSED_RECENT_CHATS = 5;

const conversationInitial = (conv: ComoConversation) =>
  (conv.title || conv.agent_name || "?").trim().charAt(0).toUpperCase() || "?";

const searchFieldSx = () => ({
  borderRadius: "10px",
  fontSize: 13.5,
  backgroundColor: color.white,
  color: color.primary_dark,
  "& input": { color: color.primary_dark, py: 1 },
  "& input::placeholder": { color: color.gray400, opacity: 1 },
  "& fieldset": { borderColor: color.gray200 },
  "&:hover fieldset": { borderColor: color.gray300 },
  "&.Mui-focused fieldset": { borderColor: color.accent, borderWidth: "1px" },
});

interface Props {
  activeConversationId: number | null;
  onNewChat: () => void;
  onSelectConversation: (id: number) => void;
  onOpenLibrary: () => void;
  onOpenAgentMemory: () => void;
  onOpenScheduledTasks: () => void;
}

// Chat navigation of Virtual Team Members, shown inside the main sidebar.
const ComoExpertsSidebarNav: FC<Props> = ({
  activeConversationId,
  onNewChat,
  onSelectConversation,
  onOpenLibrary,
  onOpenAgentMemory,
  onOpenScheduledTasks,
}) => {
  const sidebarState = useSidebarNavState();
  const collapsed = sidebarState?.collapsed ?? false;
  // Closes the mobile drawer once something was picked.
  const onNavigate = sidebarState?.onNavigate;
  const { t } = useTranslation("comoExperts");
  const navigate = useNavigate();
  const {
    data: conversations = [],
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useComoConversations();
  const deleteConversationMutation = useDeleteComoConversation();
  const updateConversationMutation = useUpdateComoConversation();
  const setConversationPinMutation = useSetConversationPin();

  const [chatSearch, setChatSearch] = useState("");
  const [searchAnchor, setSearchAnchor] = useState<HTMLElement | null>(null);
  const [convMenu, setConvMenu] = useState<{
    anchor: HTMLElement;
    conv: ComoConversation;
  } | null>(null);
  const [editingConvId, setEditingConvId] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const renameInputRef = useRef<HTMLInputElement | null>(null);

  // Rename starts from the chat menu; focus the title only once the menu has
  // closed, otherwise its focus handling leaves focus on the page body.
  useEffect(() => {
    if (editingConvId === null) return;
    const timer = window.setTimeout(() => {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }, 150);
    return () => window.clearTimeout(timer);
  }, [editingConvId]);

  const query = chatSearch.trim().toLowerCase();
  const filteredConversations = query
    ? conversations.filter(
        (c) =>
          (c.title || "").toLowerCase().includes(query) ||
          (c.agent_name || "").toLowerCase().includes(query),
      )
    : conversations;

  // Pinned conversations come first from the backend, so the list only needs
  // section headers, not reordering.
  const hasPinned = filteredConversations.some((c) => c.is_pinned);
  const firstPinnedId = filteredConversations.find((c) => c.is_pinned)?.id;
  const firstRecentId = filteredConversations.find((c) => !c.is_pinned)?.id;

  const selectConversation = (id: number) => {
    setSearchAnchor(null);
    onSelectConversation(id);
    onNavigate?.();
  };

  const togglePin = (conv: ComoConversation) => {
    setConvMenu(null);
    setConversationPinMutation.mutate({
      conversationId: conv.id,
      isPinned: !conv.is_pinned,
    });
  };

  const startRename = (conv: ComoConversation) => {
    setConvMenu(null);
    setEditingConvId(conv.id);
    setEditingTitle(conv.title || conv.agent_name || "");
  };

  const cancelRename = () => {
    setEditingConvId(null);
    setEditingTitle("");
  };

  const commitRename = () => {
    if (editingConvId === null) return;
    const title = editingTitle.trim();
    if (!title) {
      cancelRename();
      return;
    }
    updateConversationMutation.mutate(
      { conversationId: editingConvId, title },
      { onSettled: () => setEditingConvId(null) },
    );
  };

  const requestDelete = (conv: ComoConversation) => {
    setConvMenu(null);
    setPendingDeleteId(conv.id);
  };

  const confirmDelete = (confirmed: boolean) => {
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    if (!confirmed || id === null) return;
    deleteConversationMutation.mutate(id, {
      onSuccess: () => {
        if (activeConversationId === id) navigate("/como-experts");
      },
    });
  };

  const actions: NavItem[] = [
    {
      key: "newChat",
      label: t("comoExperts.newChat"),
      icon: <AddRounded />,
      onClick: onNewChat,
    },
    {
      key: "library",
      label: t("comoExperts.library", { defaultValue: "Library" }),
      icon: <MenuBookOutlined />,
      onClick: onOpenLibrary,
    },
    {
      key: "agentMemory",
      label: t("comoExperts.agentMemoryTitle"),
      icon: <PsychologyOutlined />,
      onClick: onOpenAgentMemory,
    },
    {
      key: "scheduledTasks",
      label: t("comoExperts.scheduledTasks"),
      icon: <EventRepeatOutlined />,
      onClick: onOpenScheduledTasks,
    },
  ];

  const emptyState = (
    <Box sx={{ py: 4, px: 2, textAlign: "center" }}>
      <ChatBubbleOutlineRounded
        sx={{ fontSize: 26, color: color.gray300, mb: 1 }}
      />
      <Typography sx={{ fontSize: 12.5, color: color.gray500 }}>
        {chatSearch
          ? t("comoExperts.noSearchResults", {
              defaultValue: "No matching chats",
            })
          : t("comoExperts.noChatsYet", { defaultValue: "No chats yet" })}
      </Typography>
    </Box>
  );

  const searchField = (autoFocus: boolean) => (
    <TextField
      fullWidth
      size="small"
      autoFocus={autoFocus}
      value={chatSearch}
      onChange={(e) => setChatSearch(e.target.value)}
      placeholder={t("comoExperts.searchChats", {
        defaultValue: "Search chats...",
      })}
      inputProps={{
        "aria-label": t("comoExperts.searchChats", {
          defaultValue: "Search chats...",
        }),
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchRounded sx={{ fontSize: 18, color: color.gray500 }} />
          </InputAdornment>
        ),
        endAdornment: chatSearch ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={() => setChatSearch("")}
              aria-label={t("comoExperts.clearSearch", {
                defaultValue: "Clear search",
              })}
              sx={{ p: 0.25, color: color.gray500 }}
            >
              <CloseRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </InputAdornment>
        ) : null,
        sx: searchFieldSx(),
      }}
    />
  );

  const renderConversationRow = (
    conv: ComoConversation,
    { compact = false }: { compact?: boolean } = {},
  ) => {
    const isSelected = activeConversationId === conv.id;
    const isEditing = !compact && editingConvId === conv.id;
    const menuOpen = convMenu?.conv.id === conv.id;

    return (
      <React.Fragment key={conv.id}>
        {hasPinned && conv.id === firstPinnedId && (
          <Box sx={{ pt: 1 }}>
            <DashboardMenuLabel>
              {t("comoExperts.pinned", { defaultValue: "Pinned" })}
            </DashboardMenuLabel>
          </Box>
        )}
        {hasPinned && conv.id === firstRecentId && (
          <Box sx={{ pt: 1.5 }}>
            <DashboardMenuLabel>
              {t("comoExperts.recents", { defaultValue: "Recents" })}
            </DashboardMenuLabel>
          </Box>
        )}
        <Box
          sx={{
            position: "relative",
            "&:hover .conv-menu-btn, &:focus-within .conv-menu-btn": {
              opacity: 1,
            },
          }}
        >
          <ListItemButton
            onClick={() => !isEditing && selectConversation(conv.id)}
            aria-current={isSelected ? "page" : undefined}
            sx={{
              ...itemSx(isSelected, false),
              minHeight: 46,
              px: 1,
              pr: compact ? 1 : 4.5,
              gap: 1.25,
            }}
          >
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: "8px",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700,
                backgroundColor: isSelected ? color.primary_dark : color.white,
                color: isSelected ? color.white : color.gray700,
                border: `1px solid ${isSelected ? color.primary_dark : color.gray200}`,
              }}
            >
              {conversationInitial(conv)}
            </Box>
            {isEditing ? (
              <TextField
                size="small"
                inputRef={renameInputRef}
                value={editingTitle}
                onChange={(e) => setEditingTitle(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitRename();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    cancelRename();
                  }
                }}
                inputProps={{
                  "aria-label": t("comoExperts.menu.rename", {
                    defaultValue: "Rename",
                  }),
                }}
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    fontSize: 13.5,
                    backgroundColor: color.white,
                    color: color.primary_dark,
                    borderRadius: "8px",
                  },
                  "& .MuiOutlinedInput-input": {
                    color: color.primary_dark,
                    py: 0.5,
                  },
                  "& fieldset": { borderColor: color.gray300 },
                  "& .Mui-focused fieldset": {
                    borderColor: `${color.accent} !important`,
                    borderWidth: "1px !important",
                  },
                }}
              />
            ) : (
              <ListItemText
                sx={{ my: 0, minWidth: 0 }}
                primary={conv.title || conv.agent_name}
                primaryTypographyProps={{
                  noWrap: true,
                  sx: {
                    fontSize: 13.5,
                    lineHeight: "18px",
                    fontWeight: isSelected ? 700 : 500,
                    color: color.primary_dark,
                  },
                }}
                secondary={conv.agent_name}
                secondaryTypographyProps={{
                  noWrap: true,
                  sx: {
                    fontSize: 12,
                    lineHeight: "16px",
                    color: color.gray500,
                  },
                }}
              />
            )}
          </ListItemButton>
          {!compact && (
            <IconButton
              className="conv-menu-btn"
              size="small"
              aria-label={
                isEditing
                  ? t("comoExperts.saveTitle", { defaultValue: "Save title" })
                  : t("comoExperts.chatMenu", { defaultValue: "Chat menu" })
              }
              aria-haspopup={isEditing ? undefined : "menu"}
              aria-expanded={isEditing ? undefined : menuOpen}
              onClick={(e) => {
                e.stopPropagation();
                if (isEditing) {
                  commitRename();
                  return;
                }
                setConvMenu({ anchor: e.currentTarget, conv });
              }}
              sx={{
                position: "absolute",
                right: 6,
                top: "50%",
                transform: "translateY(-50%)",
                opacity: isSelected || isEditing || menuOpen ? 1 : 0,
                color: color.gray600,
                transition: "opacity 0.15s ease",
                "&:hover": {
                  color: color.primary_dark,
                  backgroundColor: color.gray100,
                },
                "@media (hover: none)": { opacity: 1 },
              }}
            >
              {isEditing ? (
                <CheckRounded fontSize="small" />
              ) : (
                <MoreVertRounded fontSize="small" />
              )}
            </IconButton>
          )}
        </Box>
      </React.Fragment>
    );
  };

  const conversationMenu = (
    <Menu
      anchorEl={convMenu?.anchor}
      open={Boolean(convMenu)}
      onClose={() => setConvMenu(null)}
      // Rename focuses the title field, so the menu must not take focus back.
      disableRestoreFocus
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: { sx: { ...sidebarMenuPaperSx(), ml: 0.5, minWidth: 190 } },
      }}
    >
      {convMenu && (
        <MenuItem onClick={() => togglePin(convMenu.conv)} sx={{ gap: 1.5 }}>
          {convMenu.conv.is_pinned ? (
            <PushPinOutlined sx={{ fontSize: 18 }} />
          ) : (
            <PushPin sx={{ fontSize: 18 }} />
          )}
          <Typography sx={{ fontSize: 14, color: color.primary_dark }}>
            {convMenu.conv.is_pinned
              ? t("comoExperts.menu.unpin", { defaultValue: "Unpin" })
              : t("comoExperts.menu.pin", { defaultValue: "Pin" })}
          </Typography>
        </MenuItem>
      )}
      {convMenu && (
        <MenuItem onClick={() => startRename(convMenu.conv)} sx={{ gap: 1.5 }}>
          <EditOutlined sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: 14, color: color.primary_dark }}>
            {t("comoExperts.menu.rename", { defaultValue: "Rename" })}
          </Typography>
        </MenuItem>
      )}
      {convMenu && (
        <MenuItem
          onClick={() => requestDelete(convMenu.conv)}
          sx={{ gap: 1.5 }}
        >
          <DeleteOutlineRounded sx={{ fontSize: 18, color: color.red600 }} />
          <Typography sx={{ fontSize: 14, color: color.red600 }}>
            {t("comoExperts.menu.delete", { defaultValue: "Delete chat" })}
          </Typography>
        </MenuItem>
      )}
    </Menu>
  );

  const deleteDialog = (
    <ConfirmationDialog
      open={pendingDeleteId !== null}
      onClose={confirmDelete}
      title={t("comoExperts.deleteDialog.title")}
      description={t("comoExperts.deleteDialog.description")}
      content={t("comoExperts.deleteDialog.content")}
      actions={[
        {
          label: t("comoExperts.deleteDialog.no"),
          onClick: () => confirmDelete(false),
        },
        {
          label: t("comoExperts.deleteDialog.yes"),
          onClick: () => confirmDelete(true),
        },
      ]}
    />
  );

  if (collapsed) {
    const searchLabel = t("comoExperts.searchChats", {
      defaultValue: "Search chats...",
    });
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <DashboardMenu items={actions} collapsed onNavigate={onNavigate} />
        <Tooltip
          title={searchAnchor ? "" : searchLabel}
          placement="right"
          arrow
        >
          <ListItemButton
            onClick={(e) => setSearchAnchor(e.currentTarget)}
            aria-label={searchLabel}
            aria-haspopup="dialog"
            sx={{ ...itemSx(Boolean(searchAnchor), true), flexGrow: 0 }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                color: "inherit",
                "& .MuiSvgIcon-root": { fontSize: 22 },
              }}
            >
              <SearchRounded />
            </ListItemIcon>
          </ListItemButton>
        </Tooltip>

        <Divider sx={{ my: 1, mx: 1, borderColor: color.gray300 }} />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.75,
          }}
        >
          {conversations.slice(0, COLLAPSED_RECENT_CHATS).map((conv) => {
            const isSelected = activeConversationId === conv.id;
            return (
              <Tooltip
                key={conv.id}
                title={conv.title || conv.agent_name}
                placement="right"
                arrow
              >
                <IconButton
                  onClick={() => selectConversation(conv.id)}
                  aria-label={conv.title || conv.agent_name}
                  aria-current={isSelected ? "page" : undefined}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    fontSize: 13,
                    fontWeight: 700,
                    backgroundColor: isSelected
                      ? color.primary_dark
                      : color.white,
                    color: isSelected ? color.white : color.gray700,
                    border: `1px solid ${isSelected ? color.primary_dark : color.gray200}`,
                    "&:hover": {
                      backgroundColor: isSelected
                        ? color.primary_dark
                        : color.gray50,
                    },
                  }}
                >
                  {conversationInitial(conv)}
                </IconButton>
              </Tooltip>
            );
          })}
        </Box>

        <Popover
          open={Boolean(searchAnchor)}
          anchorEl={searchAnchor}
          onClose={() => setSearchAnchor(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: {
                ml: 1.5,
                p: 1.25,
                width: 320,
                borderRadius: "12px",
                backgroundColor: color.gray100,
                border: `1px solid ${color.gray200}`,
                boxShadow: "0 8px 24px rgba(16, 24, 40, 0.12)",
              },
            },
          }}
        >
          {searchField(true)}
          <List
            disablePadding
            sx={{
              mt: 1,
              maxHeight: 340,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
            }}
          >
            {filteredConversations.length === 0 && emptyState}
            {filteredConversations.map((conv) =>
              renderConversationRow(conv, { compact: true }),
            )}
          </List>
        </Popover>
        {deleteDialog}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Button
          onClick={() => {
            onNewChat();
            onNavigate?.();
          }}
          startIcon={<AddRounded />}
          sx={{
            justifyContent: "flex-start",
            minHeight: 42,
            px: 1.5,
            borderRadius: "10px",
            textTransform: "none",
            fontSize: 14,
            fontWeight: 600,
            color: color.primary_dark,
            backgroundColor: color.white,
            border: `1px solid ${color.gray200}`,
            boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
            "& .MuiButton-startIcon": { color: color.accent, mr: 1.25 },
            "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 22 },
            "&:hover": {
              backgroundColor: color.white,
              borderColor: color.accent,
            },
          }}
        >
          {t("comoExperts.newChat")}
        </Button>
        <DashboardMenu
          items={actions.slice(1)}
          collapsed={false}
          onNavigate={onNavigate}
        />
      </Box>

      <Box sx={{ pt: 2, pb: 1 }}>{searchField(false)}</Box>

      <List
        disablePadding
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          scrollbarWidth: "thin",
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
          pb: 1,
        }}
      >
        {filteredConversations.length === 0 && emptyState}
        {filteredConversations.map((conv) => renderConversationRow(conv))}
        {hasNextPage && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <Button
              size="small"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              startIcon={
                isFetchingNextPage ? (
                  <CircularProgress size={14} thickness={5} />
                ) : undefined
              }
              sx={{ textTransform: "none", color: color.gray500 }}
            >
              {t("comoExperts.loadMoreChats", {
                defaultValue: "Load older chats",
              })}
            </Button>
          </Box>
        )}
      </List>

      {conversationMenu}
      {deleteDialog}
    </Box>
  );
};

export default ComoExpertsSidebarNav;
